import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://battlebornppf.com/" },
    { "@type": "ListItem", position: 2, name: "Resources", item: "https://battlebornppf.com/resources" },
    { "@type": "ListItem", position: 3, name: "5 Mistakes Choosing a PPF Installer", item: "https://battlebornppf.com/resources/5-mistakes-choosing-ppf-installer-las-vegas" },
  ],
};

const mistakes = [
  {
    title: "Choosing Based on Price Alone",
    text: "The difference between a $700 and $1,200 hood installation isn't the film — XPEL costs roughly the same across shops. The difference is installation quality: edge wrapping vs. bulk edges, computer-cut patterns vs. hand-cut, climate-controlled bays vs. open garages. Poorly installed PPF lifts at the edges within 6–18 months in Las Vegas heat, collects dirt underneath, and requires full removal and reinstallation at your expense. The cheapest quote is often the most expensive decision.",
  },
  {
    title: "Not Verifying Certification",
    text: "XPEL, 3M, and SunTek each have certified installer programs requiring hands-on training and competency assessments. XPEL certification determines whether the 10-year manufacturer warranty against yellowing, bubbling, and delamination is honored. An installer claiming 'XPEL film' without certification may lack the training for proper installation or — in some cases — use aftermarket film. Battle Born Protection Films is XPEL certified. You can verify this through XPEL's official installer locator. Always ask to see certification before booking.",
  },
  {
    title: "Not Asking About the Install Environment",
    text: "PPF adhesive cures over 24–48 hours. During this window, dust, pet hair, pollen, and insects become permanently trapped under the film if the environment isn't controlled. In Las Vegas, where desert dust is constant, an outdoor or open-bay installation is especially risky. Ask: 'Is your installation bay climate-controlled and dust-filtered?' A quality shop will say yes and invite you to see the facility. Battle Born performs all PPF work in our enclosed, climate-controlled facility at 6960 W Russell Rd B.",
  },
  {
    title: "Skipping Paint Correction",
    text: "PPF seals the paint underneath — any swirl marks, water etching, or scratches present before application are magnified, not hidden, by the film's enhanced clarity. Low-cost quotes often omit paint correction to keep the number down, then apply film over imperfect paint. The result: a glossy film that permanently highlights every imperfection underneath. A quality installer inspects paint under LED lighting before quoting and includes necessary correction in the scope of work.",
  },
  {
    title: "No Written Warranty",
    text: "A legitimate PPF installation includes two warranties: the manufacturer's material warranty (XPEL offers 10 years) and the installer's workmanship warranty covering edge lifting, defects, and contamination. Both should be documented and provided to you in writing. Verbal warranties are unenforceable. Ask before booking: 'What is your workmanship warranty and what does it cover?' If the installer can't answer clearly, that's a red flag regardless of price.",
  },
];

const PpfInstallerMistakes = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="5 Mistakes When Choosing a PPF Installer in Las Vegas"
        description="Avoid these 5 costly PPF installer mistakes in Las Vegas. What to check, what to ask, and why XPEL certification matters."
        canonical="https://battlebornppf.com/resources/5-mistakes-choosing-ppf-installer-las-vegas"
        schema={breadcrumbSchema}
        publishedDate="2025-11-01"
        modifiedDate="2026-02-15"
      />

      <article className="py-16 px-4">
        <div className="max-w-3xl mx-auto space-y-12">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-accent transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/resources" className="hover:text-accent transition-colors">Resources</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-foreground">5 Mistakes Choosing a PPF Installer</span>
          </nav>

          {/* Header */}
          <header>
            <h1 className="text-3xl md:text-4xl font-heading font-bold">
              5 Mistakes People Make When Choosing a PPF Installer in Las Vegas
            </h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2 mb-8">
              By Battle Born Protection Films Team · Published Nov 2025 · Updated Feb 2026
            </div>
            <p className="text-lg font-medium leading-relaxed">
              Choosing the wrong PPF installer in Las Vegas can cost $2,000–$8,000 and leave your vehicle worse than before. These are the five most common mistakes Las Vegas vehicle owners make, and what to look for instead to ensure a flawless, warranty-backed installation.
            </p>
          </header>

          {/* Mistake Cards */}
          {mistakes.map((mistake, index) => (
            <section key={index} className="relative border border-border rounded-lg p-6 md:p-8">
              <span className="absolute -top-4 left-6 bg-accent text-accent-foreground text-sm font-bold px-3 py-1 rounded-full">
                Mistake {index + 1}
              </span>
              <h2 className="text-2xl font-heading font-semibold mb-4 mt-2">
                Mistake {index + 1} — {mistake.title}
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground">
                {mistake.text}
              </p>
            </section>
          ))}

          {/* How to Find a Qualified Installer */}
          <section>
            <h2 className="text-2xl font-heading font-semibold mb-4">How to Find a Qualified Installer</h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Use XPEL's installer locator at xpel.com to find certified shops in Las Vegas. Read reviews mentioning film edge quality, longevity, and post-install support. Ask to see completed work on vehicles similar to yours. Request to see the install bay. Get warranty terms in writing. Battle Born Protection Films meets all these standards: XPEL certified, climate-controlled facility, written warranties, and a portfolio of luxury and exotic vehicle work. Call{" "}
              <a href="tel:+17025599365" className="text-accent font-semibold hover:underline">(702) 559-9365</a>{" "}
              or visit us at 6960 W Russell Rd B, Monday through Friday, 8 AM to 5 PM.
            </p>
          </section>

          {/* CTA */}
          <section className="bg-accent/10 rounded-xl p-8 text-center space-y-4">
            <h2 className="text-2xl font-heading font-bold">Choose With Confidence</h2>
            <p className="text-muted-foreground">
              Get a free consultation from an XPEL-certified installer you can trust.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild variant="hero" size="lg">
                <a href="tel:+17025599365">Call (702) 559-9365</a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact">Request a Quote</Link>
              </Button>
            </div>
          </section>
        </div>
      </article>
    </div>
  );
};

export default PpfInstallerMistakes;
